/**
 * The client's analyses as the screens show them: one entry per analysis,
 * its reports inside it.
 *
 * An analysis's status is rolled up from its reports, so it never reads as
 * done while part of it is not: running or waiting if any report is, failed
 * if any report failed, not run if any report has not run, completed only
 * when every report completed. Findings and rows add up across reports.
 * Its data is sales only or returns only when every report reads that, and
 * sales and returns otherwise.
 */

import type {
	AnalysisDataType,
	AnalysisOverviewRow,
	ClientAnalysis,
} from '../../schemas';
import type { ModuleCode } from '../modules/modules';
import { count, dateTime, durationLabel } from '../../utils/format';

/** Which data an analysis reads, in the words every screen uses. */
export const DATA_TYPE_LABEL: Record<AnalysisDataType, string> = {
	SALES_ONLY: 'Sales only',
	RETURNS_ONLY: 'Returns only',
	BOTH: 'Sales and returns',
};

/** The data an analysis reads, as the screen words it: an H2R audit has one
 *  data set (the employee master), not sales and returns. */
export function dataTypeLabel(
	type: AnalysisDataType,
	module: ModuleCode = 'O2C',
): string {
	return module === 'H2R' ? 'Employee master' : DATA_TYPE_LABEL[type];
}

export interface AnalysisReportRow {
	/** 1-based, in the client's order. */
	index: number;
	label: string;
	row: AnalysisOverviewRow;
}

export interface AnalysisRow {
	number: number;
	title: string;
	/** The client's own description of the rule — the ⓘ beside the name. */
	description: string;
	/** Set when the analysis is on hold; `reports` is then empty. */
	onHold: string | null;
	reports: AnalysisReportRow[];
	/** The reports rolled up into one row — what the status tag, the filters
	 *  and the counts read. Null for an analysis on hold. */
	rollup: AnalysisOverviewRow | null;
}

const STATUS_ORDER: AnalysisOverviewRow['status'][] = [
	'RUNNING',
	'QUEUED',
	'FAILED',
	'NOT_RUN',
	'COMPLETED',
];

const sumOrNull = (values: Array<number | null>): number | null => {
	const known = values.filter((value): value is number => value !== null);
	return known.length === 0
		? null
		: known.reduce((total, value) => total + value, 0);
};

const latest = (values: Array<string | null>): string | null =>
	values
		.filter((value): value is string => value !== null)
		.sort()
		.at(-1) ?? null;

const earliest = (values: Array<string | null>): string | null =>
	values.filter((value): value is string => value !== null).sort()[0] ?? null;

export function rollUp(
	analysis: Pick<ClientAnalysis, 'number' | 'title'>,
	rows: [AnalysisOverviewRow, ...AnalysisOverviewRow[]],
): AnalysisOverviewRow {
	const first = rows[0];
	const status =
		STATUS_ORDER.find((candidate) =>
			rows.some((row) => row.status === candidate),
		) ?? first.status;
	const allBlocked = rows.every((row) => row.availability === 'BLOCKED');
	const availability = allBlocked
		? 'BLOCKED'
		: rows.some((row) => row.availability === null)
			? null
			: 'READY';
	const missing = new Map<string, { key: string; label: string }>();
	for (const row of rows)
		for (const column of row.missingColumns)
			missing.set(column.key, column);
	const completed = rows.filter((row) => row.status === 'COMPLETED');
	return {
		...first,
		ref: `#${analysis.number}`,
		name: analysis.title,
		dataType: rows.every((row) => row.dataType === first.dataType)
			? first.dataType
			: 'BOTH',
		status,
		availability,
		missingColumns: allBlocked ? [...missing.values()] : [],
		blockReason: allBlocked ? first.blockReason : null,
		runAt: latest(rows.map((row) => row.runAt)),
		startedAt: earliest(rows.map((row) => row.startedAt)),
		completedAt:
			status === 'COMPLETED'
				? latest(rows.map((row) => row.completedAt))
				: null,
		durationMs:
			status === 'COMPLETED'
				? sumOrNull(rows.map((row) => row.durationMs))
				: null,
		rowsAnalysed: first.rowsAnalysed,
		resultRows: sumOrNull(completed.map((row) => row.resultRows)),
		exceptionFindings: sumOrNull(
			completed.map((row) => row.exceptionFindings),
		),
		supportingRows: sumOrNull(completed.map((row) => row.supportingRows)),
		outdated: rows.some((row) => row.outdated),
		error: rows.find((row) => row.status === 'FAILED')?.error ?? null,
		hasResult: rows.some((row) => row.hasResult),
		hasExcel: rows.length > 0 && rows.every((row) => row.hasExcel),
		excelUnavailableReason:
			rows.find((row) => !row.hasExcel)?.excelUnavailableReason ?? null,
	};
}

/** Every analysis in the client's order. Analyses on hold (#11, #30, #31)
 *  are not shown, and nor is one none of whose reports this audit includes. */
export function analysisRows(
	rows: AnalysisOverviewRow[],
	analyses: ClientAnalysis[],
): AnalysisRow[] {
	const bySlug = new Map(rows.map((row) => [row.slug, row]));
	const out: AnalysisRow[] = [];
	for (const analysis of analyses) {
		if (analysis.onHold !== null) continue;
		const reports: AnalysisReportRow[] = [];
		analysis.reports.forEach((report, index) => {
			const row = bySlug.get(report.slug);
			if (row)
				reports.push({ index: index + 1, label: report.label, row });
		});
		const [head, ...rest] = reports.map((report) => report.row);
		if (!head) continue;
		out.push({
			number: analysis.number,
			title: analysis.title,
			description: analysis.description,
			onHold: null,
			reports,
			rollup: rollUp(analysis, [head, ...rest]),
		});
	}
	return out;
}

/* ═══ preview facts ════════════════════════════════════════════════════════
   The preview drawer's facts and the Excel download reason.                */

/** Why an analysis's Excel can't be downloaded right now, or null when it can.
 *  The same order the list's row button uses. */
export function excelDownloadReason(
	row: Pick<
		AnalysisOverviewRow,
		'status' | 'hasExcel' | 'excelUnavailableReason'
	> & {
		listStatus: string;
	},
	anotherDownloadRunning: boolean,
): string | null {
	if (row.listStatus !== 'COMPLETED')
		return 'Available once the analysis has completed.';
	if (!row.hasExcel)
		return (
			row.excelUnavailableReason ??
			'There is no Excel file for this result.'
		);
	if (anotherDownloadRunning) return 'Another download is in progress.';
	return null;
}

export interface PreviewFact {
	label: string;
	value: string;
}

function findingsValue(row: AnalysisOverviewRow): string {
	if (row.exceptionFindings !== null) return count(row.exceptionFindings);
	if (row.exceptionCalculation === 'DISABLED')
		return 'Not calculated (disabled for this analysis)';
	if (row.exceptionCalculation === 'NOT_IMPLEMENTED')
		return 'Not calculated (no exception rules yet)';
	return 'Not available for this result';
}

/** The preview's facts for one analysis, from the overview row only. */
export function previewFacts(
	rollup: AnalysisOverviewRow,
	module: ModuleCode = 'O2C',
): {
	run: PreviewFact[];
	rows: PreviewFact[];
	availability: PreviewFact[];
} {
	return {
		run: [
			{ label: 'Started', value: dateTime(rollup.startedAt) },
			{ label: 'Completed', value: dateTime(rollup.completedAt) },
			{
				label: 'Duration',
				value:
					rollup.status === 'COMPLETED'
						? durationLabel(rollup.durationMs)
						: '—',
			},
		],
		rows: [
			{ label: 'Rows analysed', value: count(rollup.rowsAnalysed) },
			{ label: 'Result rows', value: count(rollup.resultRows) },
			{ label: 'Exception findings', value: findingsValue(rollup) },
			{ label: 'Supporting rows', value: count(rollup.supportingRows) },
		],
		availability: [
			{
				label: 'Based on',
				value: dataTypeLabel(rollup.dataType, module),
			},
			{
				label: 'Data check',
				value:
					rollup.availability === 'READY'
						? 'Ready'
						: rollup.availability === 'BLOCKED'
							? 'Blocked'
							: 'Not checked yet',
			},
		],
	};
}
